// REACT
import { useState } from "react";

// MATERIAL UI
import { Box, Skeleton, Stack, Typography } from "@mui/material";

// COMPONENTS
import ContainedButton from "src/components/common/ContainedButton";

// REDUX
import api from "src/store/features/api";
import { TaxBitUpdateAccountDto } from "src/store/features/api/generated";

// STYLES
import { BORDER, Card, NAVY, SALMON, TEXT_MUTED, TEXT_SECONDARY } from "./styles";

type DispositionMethod = TaxBitUpdateAccountDto["disposition_method"];

interface MethodOption {
  id: DispositionMethod;
  title: string;
  description: string;
}

const OPTIONS: MethodOption[] = [
  {
    id: "HIFO",
    title: "HIFO — highest in, first out",
    description: "Disposes the most expensive lots first, typically minimising near-term gains.",
  },
  {
    id: "FIFO",
    title: "FIFO — first in, first out",
    description: "Disposes your oldest lots first. Most likely to produce long-term gains.",
  },
  {
    id: "LIFO",
    title: "LIFO — last in, first out",
    description: "Disposes your most recently acquired lots first.",
  },
  // Not in the PRD mockups, but TaxBit supports it and some accounts are already set to it —
  // dropping it as an option would strand them.
  {
    id: "LOFO",
    title: "LOFO — lowest in, first out",
    description: "Disposes your least expensive lots first.",
  },
];

const isDispositionMethod = (value: unknown): value is DispositionMethod =>
  OPTIONS.some((option) => option.id === value);

const methodLabel = (method: DispositionMethod) =>
  OPTIONS.find((option) => option.id === method)?.title.split(" — ")[0] ?? method;

const serverErrorMessage = (error: unknown): string | undefined => {
  const message = (error as { data?: { message?: unknown } })?.data?.message;
  return typeof message === "string" && message.trim() ? message : undefined;
};

const DispositionMethodCard = () => {
  const accountQuery = api.useGetTaxAccountQuery();
  const [updateTaxAccount, { isLoading: isSaving }] = api.useUpdateTaxAccountMutation();
  const [draft, setDraft] = useState<DispositionMethod | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [savedMessage, setSavedMessage] = useState("");

  const current = isDispositionMethod(accountQuery.data?.disposition_method)
    ? accountQuery.data?.disposition_method
    : undefined;
  const selected = draft ?? current;
  // Whatever TaxBit holds at account level; no badge if it reports none.
  const defaultMethod = accountQuery.data?.default_disposition_method;
  const disabled = accountQuery.isLoading || isSaving;
  const isDirty = Boolean(draft) && draft !== current;

  const handleSelect = (method: DispositionMethod) => {
    if (disabled || method === selected) return;
    setErrorMessage("");
    setSavedMessage("");
    setDraft(method);
  };

  const handleSave = async () => {
    if (!isDirty || !draft || disabled) return;
    setErrorMessage("");
    setSavedMessage("");
    try {
      await updateTaxAccount({ taxBitUpdateAccountDto: { disposition_method: draft } }).unwrap();
      setDraft(null);
      setSavedMessage(`Saved. Your disposition method is now ${methodLabel(draft)}.`);
    } catch (error: unknown) {
      console.warn("Unable to update disposition method", error);
      setErrorMessage(
        serverErrorMessage(error) ??
          "Unable to update your disposition method right now. Please try again.",
      );
    }
  };

  return (
    <Card sx={{ p: 3.25, display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Typography sx={{ fontSize: 17, fontWeight: 600, color: NAVY, lineHeight: 1.3 }}>
        Disposition method
      </Typography>
      <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY, lineHeight: 1.55, pb: 1.5 }}>
        Determines which lots are sold first when you dispose of an asset, and therefore the gain
        reported on your 1099-DA.
      </Typography>

      <Stack spacing={1.25}>
        {accountQuery.isLoading
          ? Array.from({ length: 3 }).map((_, idx) => (
              <Skeleton key={`method-skeleton-${idx}`} variant="rounded" height={76} />
            ))
          : OPTIONS.map((option) => {
              const isSelected = selected === option.id;
              return (
                <Stack
                  key={option.id}
                  direction="row"
                  spacing={1.75}
                  alignItems="flex-start"
                  role="radio"
                  aria-checked={isSelected}
                  tabIndex={disabled ? -1 : 0}
                  onClick={() => handleSelect(option.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSelect(option.id);
                    }
                  }}
                  sx={{
                    px: 2.25,
                    py: 2,
                    border: `1.5px solid ${isSelected ? SALMON : BORDER}`,
                    backgroundColor: isSelected ? "#FFF6F6" : "#fff",
                    borderRadius: "6px",
                    cursor: disabled ? "default" : "pointer",
                    opacity: isSaving && !isSelected ? 0.6 : 1,
                    transition: "border-color 120ms, background-color 120ms",
                    "&:hover": disabled ? undefined : { borderColor: SALMON },
                  }}
                >
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      flex: "none",
                      mt: "1px",
                      borderRadius: "50%",
                      border: `1.5px solid ${isSelected ? SALMON : BORDER}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        backgroundColor: isSelected ? SALMON : "transparent",
                      }}
                    />
                  </Box>
                  <Stack spacing={0.5}>
                    <Typography
                      sx={{ fontSize: 15, fontWeight: 500, color: NAVY, lineHeight: 1.3 }}
                    >
                      {option.title}
                      {option.id === defaultMethod && (
                        <Typography
                          component="span"
                          sx={{
                            ml: 1,
                            fontSize: 11,
                            fontWeight: 500,
                            color: TEXT_MUTED,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                          }}
                        >
                          Default
                        </Typography>
                      )}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, lineHeight: 1.5 }}>
                      {option.description}
                    </Typography>
                  </Stack>
                </Stack>
              );
            })}
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        gap={1.5}
        sx={{ pt: 2 }}
      >
        <Typography
          sx={{
            fontSize: 12,
            color: errorMessage ? "error.main" : TEXT_MUTED,
            minHeight: 18,
            lineHeight: 1.5,
          }}
        >
          {errorMessage ||
            (isSaving
              ? "Saving your method…"
              : isDirty
              ? "Saving confirms you have elected this change. We keep a record of it."
              : savedMessage)}
        </Typography>
        <Box sx={{ flex: "none" }}>
          <ContainedButton
            color="salmon"
            width="130px"
            height="38px"
            disabled={!isDirty || disabled}
            onClick={() => void handleSave()}
          >
            {isSaving ? "Saving…" : "Save changes"}
          </ContainedButton>
        </Box>
      </Stack>
    </Card>
  );
};

export default DispositionMethodCard;
